import { createContext, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useLocation } from 'react-router-dom'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import Cookies from 'js-cookie'
import InstituteSelectionModal from './InstituteSelectionModal'
import {
  clearAuthCookies,
  hasValidInstituteIdCookie,
  removeInstituteIdCookie,
  setDefaultInvalidInstituteIdCookie,
} from '../utils/instituteIdCookie'
import { syncParentContextAfterLogin } from './commonComponents/homeworkApi'

const AuthContext = createContext()
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isLoginSuccessfull, setIsLoginSuccessful] = useState(false)
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const userLogged = Cookies.get('user')
    // console.log('🔍 Checking if user is logged in:', userLogged)

    if (userLogged) {
      try {
        const parsedUser = JSON.parse(userLogged)
        if (parsedUser?.token && parsedUser?.user_type) {
          setUser(parsedUser)
          // console.log('✅ User restored from cookies:', parsedUser)
          
          if (parsedUser?.user_type === 'admin') {
            const instituteConfirmed = sessionStorage.getItem('institute_confirmed')
            
            // If institute_id cookie exists but no sessionStorage flag, it's a stale cookie
            // Clear it and treat as if no institute selected
            if (hasValidInstituteIdCookie() && !instituteConfirmed) {
              // console.log('⚠️ Stale institute_id cookie detected (no session confirmation). Clearing it.')
              removeInstituteIdCookie()
              Cookies.remove('name')
              setDefaultInvalidInstituteIdCookie()
            }
            
            // Check again after potential cleanup
            if (!hasValidInstituteIdCookie()) {
              // console.log('⚠️ Admin user but no institute selected.')
              
              // If currently on Dashboard route, redirect to LoginSignup immediately
              // Use window.location.replace for immediate, unblockable redirect
              if (window.location.pathname === '/Dashboard' || window.location.pathname === '/') {
                // console.log('🚫 On Dashboard without institute. Redirecting to LoginSignup.')
                Cookies.remove('name')
                Cookies.remove('institute_name')
                Cookies.remove('institute_logo')
                sessionStorage.removeItem('institute_confirmed')
                sessionStorage.removeItem('institute_confirmed_timestamp')
                setDefaultInvalidInstituteIdCookie()
                // Use window.location for immediate redirect that can't be blocked
                window.location.replace('/LoginSignup')
                return
              }
              
              // Otherwise show modal
              // console.log('📋 Showing institute selection modal.')
              setIsLoginSuccessful(true)
            }
          }

          const restoredUserType = String(parsedUser?.user_type || '').toLowerCase()
          if (restoredUserType === 'parents' || restoredUserType === 'parent') {
            const missingSession = !Cookies.get('session_name') && !Cookies.get('session')
            if (missingSession || !hasValidInstituteIdCookie()) {
              syncParentContextAfterLogin()
            }
          }
        } else {
          Cookies.remove('user')
          // console.log('❌ Invalid cookie data removed')
        }
      } catch (error) {
        Cookies.remove('user')
        console.error('❌ Error parsing user from cookies:', error)

        // setUser(null)
      }
    }
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // const login = async (loginData) => {
  //   console.log('📤 Login request sent:', loginData)

  //   try {
  //     const response = await ApiEndpoints.login(loginData)
  //     const result = response?.data?.result
  //     console.log('📥 Login response received:', result)

  //     // Check if login was successful
  //     if (!result || result === 'User not found') {
  //       toast.error(response.data.message || 'Login failed')
  //       return
  //     }

  //     // Extract token and user type
  //     const { token, user_type, institute_id } = result

  //     // Store user information in state and cookies
  //     const userData = { token, user_type, } // Correctly structured object
  //     Cookies.set('user', JSON.stringify(userData))
  //     setUser(userData)

  //     toast.success(`${response.data.message}.`)

  //     if (user_type === 'admin') {
  //       // toast.success(' Please select an institute.')
  //       setIsLoginSuccessful(true)
  //       // navigate('/dashboard')
  //     } else if (user_type === 'teacher'&& institute_id) {
  //       // toast.success(response.data.message)
  //       Cookies.set('institute_id', institute_id)
  //       console.log('Institute ID saved in cookie:', institute_id)
  //       setIsLoginSuccessful(false)
  //       navigate('/AssignClass')
  //     }
  //     else if(user_type==="parents"){
  //       setIsLoginSuccessful(false)
  //       navigate("/parent/dashboard")
  //     }
  //   } catch (error) {
  //     console.error('❌ Login failed:', error)
  //     toast.error(error.response?.data?.message || 'Login failed')
  //   }
  // }

  const applyLoginResult = (result, message) => {
    const {
      token,
      user_type,
      roles,
      institute_id,
      id,
      user_id,
      user_name,
      institute_type,
    } = result
    const userData = { token, user_type, roles, institute_id }
    if (user_name) {
      userData.user_name = user_name
    }
    const resolvedInstituteType = String(institute_type || '')
      .trim()
      .toLowerCase()
    if (resolvedInstituteType) {
      userData.institute_type = resolvedInstituteType
    }
    const resolvedId = id ?? user_id
    if (resolvedId != null && resolvedId !== '') {
      userData.id = Number(resolvedId) || resolvedId
    }
    Cookies.set('user', JSON.stringify(userData))
    // Admin: institute_id cookie only after InstituteSelectionModal confirm (not at login)
    if (user_type === 'admin') {
      Cookies.remove('name')
      Cookies.remove('institute_logo')
      sessionStorage.removeItem('institute_confirmed')
      sessionStorage.removeItem('institute_confirmed_timestamp')
      setDefaultInvalidInstituteIdCookie()
    } else if (institute_id) {
      Cookies.set('institute_id', institute_id, { expires: 7 })
    }
    if (resolvedInstituteType) {
      Cookies.set('institute_type', resolvedInstituteType, { expires: 7 })
    } else {
      Cookies.remove('institute_type')
    }
    Cookies.set('user_type', user_type, { expires: 7 })
    Cookies.set('roles', JSON.stringify(roles), { expires: 7 })

    setUser(userData)
    toast.success(message || 'Logged in successfully')

    const normalizedUserType = String(user_type || '').toLowerCase()
    if (normalizedUserType === 'parents' || normalizedUserType === 'parent') {
      syncParentContextAfterLogin()
    }

    if (user_type === 'admin') {
      setIsLoginSuccessful(true) // open modal
    }
  }

  const login = async (loginData) => {
    try {
      const response = await ApiEndpoints.login(loginData)
      const result = response?.data?.result

      if (!result || result === 'User not found') {
        toast.error(response.data.message || 'Login failed')
        return
      }

      applyLoginResult(result, response.data.message)
    } catch (error) {
      toast.error(error.response?.data?.result || error.response?.data?.message || 'Login failed')
    }
  }

  const loginWithGoogle = async (credential) => {
    try {
      const response = await ApiEndpoints.googleLogin(credential)
      const result = response?.data?.result

      if (!result || result === 'User not found') {
        toast.error(response.data.message || 'Google login failed')
        return
      }

      applyLoginResult(result, response.data.message || 'Logged in with Google')
    } catch (error) {
      toast.error(
        error.response?.data?.result ||
          error.response?.data?.message ||
          'Google login failed',
      )
    }
  }


  const logout = ({ invalidInstitute = false } = {}) => {
    // Clear cookies before React state updates so InstituteContext cannot re-write institute_id
    clearAuthCookies()
    setUser(null)
    setIsLoginSuccessful(false)
    if (invalidInstitute) {
      console.error('Your session is invalid. Please sign in again.')
    } else {
      toast.success('Logged out successfully!')
    }
    // Full navigation so no provider effect runs on the old route with stale cookies
    window.location.replace('/LoginSignup')
  }

  useEffect(() => {
    if (isLoginSuccessfull && user?.user_type === 'admin') {
      navigate(window.location.pathname, { replace: true })
    }
  }, [isLoginSuccessfull, user])

  const signUp = async (signupData) => {
    try {
      const response = await ApiEndpoints.signUp(signupData)
      toast.success(response.data.message)
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error) {
      const errorMessage = error.response?.data?.message

      if (errorMessage?.includes('Duplicate entry')) {
        toast.error('Email already exists. Please use a different email.')
      } else {
        toast.error(errorMessage || 'Signup failed')
      }
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token:user?.token,
        institute_id:user?.institute_id,
        institute_type: user?.institute_type,
        roles:user?.roles,
        user_type:user?.user_type,
        login,
        loginWithGoogle,
        signUp,
        logout,
        courses,
        setCourses,
        classes,
        setClasses,
        students,
        setStudents,
      }}
    >
      {loading ? (
        <div>Loading...</div>
      ) : // On LoginSignup: show login form unless admin must pick institute (modal opens after login / restore)
      location.pathname === '/LoginSignup' &&
      !(isLoginSuccessfull && user?.user_type === 'admin' && !hasValidInstituteIdCookie()) ? (
        <>{children}</>
      ) : // If on Dashboard/root route without institute_id, let ProtectedDashboard handle redirect
      (location.pathname === '/Dashboard' || location.pathname === '/') && user?.user_type === 'admin' && !hasValidInstituteIdCookie() ? (
        <>{children}</> // ProtectedDashboard will redirect
      ) : // Show modal only if admin user without institute_id and not on LoginSignup/Dashboard
      ((isLoginSuccessfull && user?.user_type === 'admin') || 
          (user?.user_type === 'admin' && !hasValidInstituteIdCookie())) ? (
        <InstituteSelectionModal 
          onClose={() => {
            setIsLoginSuccessful(false)
            // If still no institute selected after closing, ensure redirect to LoginSignup
            if (!hasValidInstituteIdCookie()) {
              // Clear any stale cookies and redirect
              Cookies.remove('name')
              setDefaultInvalidInstituteIdCookie()
              navigate('/LoginSignup', { replace: true })
            }
          }} 
        />
      ) : (
        <>{children}</>
      )}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
